
import { useUrlProcessor } from "@/hooks/useUrlProcessor";
import { useSubscriptionChecker } from "@/hooks/useSubscriptionChecker";
import { SubscriptionStatus } from "@/services/subscription";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import { useTransformation } from "@/hooks/useTransformation";

interface RecipeSubmitHandlerProps {
  user: any;
  subscriptionStatus: SubscriptionStatus | null;
  onLoadSubscriptionStatus: (userId: string) => Promise<SubscriptionStatus | null>;
  forceRefresh?: () => void;
  setShowPricingModal: (show: boolean) => void;
  setIsLoading: (loading: boolean) => void;
  setRecipeData: (data: any) => void;
}

export const useRecipeSubmitHandler = ({
  user,
  subscriptionStatus,
  onLoadSubscriptionStatus,
  forceRefresh,
  setShowPricingModal,
  setIsLoading,
  setRecipeData
}: RecipeSubmitHandlerProps) => {
  // Use our subscription status hook
  const { 
    lastCheckedStatus, 
    setLastCheckedStatus,
    isCheckingSubscriptionRef,
    hasCheckedSubscriptionRef,
    transformationCountRef,
    isSubscriptionActive,
    hasFreeTransforms,
    isLocallyConfirmedActive
  } = useSubscriptionStatus(subscriptionStatus, user);

  // Use our URL processor hook
  const { isProcessingUrl, setIsProcessingUrl, processUrl } = useUrlProcessor(user);

  // Use our subscription checker hook
  const { checkSubscriptionStatus } = useSubscriptionChecker({
    lastCheckedStatus,
    isSubscriptionActive,
    hasFreeTransforms,
    isLocallyConfirmedActive,
    hasCheckedSubscriptionRef,
    isCheckingSubscriptionRef,
    setLastCheckedStatus,
    forceRefresh,
    onLoadSubscriptionStatus
  });

  // Use our transformation hook
  const { startTransformation } = useTransformation(
    user,
    setIsLoading,
    setRecipeData,
    onLoadSubscriptionStatus,
    forceRefresh,
    lastCheckedStatus,
    setLastCheckedStatus,
    isCheckingSubscriptionRef
  );

  const handleRecipeSubmit = async (url: string) => {
    if (isProcessingUrl) return;
    setIsProcessingUrl(true);

    try {
      // Process URL first
      if (!processUrl(url)) {
        setIsProcessingUrl(false);
        return;
      }

      // Increment the local transformation count
      transformationCountRef.current += 1;
      console.log(`Transformation #${transformationCountRef.current} requested`);

      // Check subscription status
      const canProceed = await checkSubscriptionStatus(user.id);
      
      if (canProceed) {
        await startTransformation(url);
      } else {
        console.log('No active subscription or free transforms, showing pricing modal');
        localStorage.removeItem('subscription_confirmed_active');
        setShowPricingModal(true);
      }
    } catch (error) {
      console.error('Error in recipe submission:', error);
    } finally {
      setIsProcessingUrl(false);
    }
  };

  return { handleRecipeSubmit };
};
